// The banner, as data.
//
// A pure function returning the configuration object — the library's TYPE and
// nothing else of it — so the shape of the question (the categories, the three
// buttons, the wording, every flag whose default is wrong for us) is a table
// the unit suite reads rather than something only a browser can show. `boot.ts`
// is the only module that runs the library.
//
// Translations are inline and English-only. The library can fetch a language
// file over the network instead; it is not allowed to, because a banner that
// makes a request before anybody has consented to anything is the one request
// on the site that has to be impossible rather than merely absent.

import type { CookieConsentConfig } from "vanilla-cookieconsent";
import {
  ANALYTICS_CATEGORY,
  CONSENT_COOKIE,
  CONSENT_COOKIE_DAYS,
  CONSENT_REVISION,
  type ConsentCategory,
  cookiesInCategory,
  describeLifetime,
  displayName,
  EXPERIMENTS_CATEGORY,
  FIRST_TOUCH_COOKIE,
  FIRST_TOUCH_DAYS,
  GA_CLIENT_COOKIE,
  VISITOR_COOKIE,
} from "./categories.ts";

export interface ConsentCallbacks {
  /**
   * Every load that carries a valid answer, and the moment one is first given.
   *
   * This is what SWITCHES MEASUREMENT ON, and it is deliberately not the
   * first-answer callback: that one fires once in a visitor's life, so a
   * returning consenter would be counted by nothing at all.
   */
  readonly onReady: (granted: readonly string[]) => void;
  /** The very first answer, once ever. What the accept/reject/dismiss count is. */
  readonly onFirstAnswer: (granted: readonly string[]) => void;
  /** A later change, including a withdrawal. */
  readonly onChanged: (granted: readonly string[]) => void;
}

/**
 * What the library is given — typed by the library's own declaration, so a
 * setting renamed by a dependency bump is a build failure here rather than a
 * silently ignored key in the one file whose job is to state policy.
 */
export type ConsentConfig = CookieConsentConfig;

/** The column headings of the table the preferences panel prints. */
const COOKIE_TABLE_HEADERS = {
  name: "Name",
  kind: "Type",
  purpose: "What it is for",
  lifetime: "Kept for",
  setBy: "Set by",
} as const;

/**
 * One category's rows of the panel's cookie table, taken from the one list.
 *
 * THIS IS WHAT MAKES THE LIST'S CLAIM TRUE. Before this, the panel carried
 * hand-written prose and nothing rendered the table at all, so the table could
 * — and did — say a cookie lived thirty days while the setter said seven, with
 * nothing in the product to contradict it. Printed here, a wrong row is visible
 * to the first person who opens the panel.
 */
function cookieTableFor(category: ConsentCategory): { body: Record<string, string>[] } {
  return {
    body: cookiesInCategory(category).map((entry) => ({
      name: displayName(entry),
      kind: entry.kind === "cookie" ? "Cookie" : "Browser storage",
      purpose: entry.purpose,
      lifetime: describeLifetime(entry.lifetimeSeconds),
      setBy: entry.setBy,
    })),
  };
}

export function consentConfig(callbacks: ConsentCallbacks): ConsentConfig {
  return {
    // Nothing non-essential runs until it is asked for. The library's default,
    // stated because it is the whole premise.
    mode: "opt-in",
    revision: CONSENT_REVISION,

    // ITS DEFAULT IS TRUE AND ITS DEFAULT IS WRONG FOR US. Left on, the library
    // finds `<script type="text/plain" data-category="…">` elements and
    // re-creates them as INLINE scripts — which needs the `'unsafe-inline'`
    // this delivery exists to stop needing. Everything non-essential is loaded
    // through this app's own code from the callbacks above instead.
    manageScriptTags: false,

    // Its default is true, and true means the banner is not shown to anything
    // whose user agent looks like a crawler OR that reports
    // `navigator.webdriver` — which is every browser an automated run drives.
    // Left on, the one thing that could prove the banner works is the one thing
    // that cannot see it. A crawler meeting a fixed overlay costs nothing: the
    // page's content is in the document either way.
    hideFromBots: false,

    // The library erases the cookies of a category that is refused or withdrawn.
    // It can only reach what a browser can reach, which is why the first-touch
    // cookie is cleared by the server in the change callback as well — it is
    // HttpOnly, and that is the point of it.
    autoClearCookies: true,

    cookie: {
      name: CONSENT_COOKIE,
      expiresAfterDays: CONSENT_COOKIE_DAYS,
      path: "/",
      sameSite: "Lax",
      // Honoured only on https, so a local run still gets a cookie.
      secure: true,
    },

    guiOptions: {
      consentModal: {
        // `box` is also what makes the close control exist at all: the library
        // draws it only for a box layout that has a title and a label for it.
        layout: "box",
        // BOTTOM LEFT, and not the centre. The banner is a fixed overlay, so it
        // shifts nothing — but whatever it is over cannot be clicked while it
        // is up, and a centred box on a wide screen is over the one column the
        // page's own content is in. On the short pages that is the submit
        // button of the form somebody came to use. The margin is the part of
        // the viewport nothing else is using.
        //
        // On a narrow screen the box is full width and covers the bottom of the
        // page whatever this says, which is true of every banner there is and
        // is the reason the answer is one click on the layer already on screen.
        position: "bottom left",
        // The three buttons are drawn the same size, weight, shape and font.
        // This is the setting that decides it, and a banner whose refuse button
        // is quieter than its accept button is the single most common finding
        // in every published banner review.
        equalWeightButtons: true,
        flipButtons: false,
      },
      preferencesModal: {
        layout: "box",
        equalWeightButtons: true,
        flipButtons: false,
      },
    },

    onFirstConsent: ({ cookie }) => {
      callbacks.onFirstAnswer(cookie.categories ?? []);
    },
    // Fires on the first answer AND on every later page load that carries one.
    onConsent: ({ cookie }) => {
      callbacks.onReady(cookie.categories ?? []);
    },
    onChange: ({ cookie }) => {
      callbacks.onChanged(cookie.categories ?? []);
    },

    categories: {
      necessary: {
        // Always on and not a question: without it the site cannot answer, and
        // the banner's own memory is the textbook example of storage that is
        // exempt. Marked read-only so it cannot be presented as a choice that
        // is not one.
        enabled: true,
        readOnly: true,
      },
      [ANALYTICS_CATEGORY]: {
        // OFF. No pre-ticked box, ever: silence is not consent, and a category
        // that starts on is the second most common finding after an unequal
        // reject button.
        enabled: false,
        // The Google Analytics cookies as a pattern — `_ga` and one `_ga_<id>`
        // per property — because the second name carries the property's own
        // id. The library erases them on this host and on the parent domain,
        // which is where the tag sets them.
        autoClear: {
          cookies: [
            { name: FIRST_TOUCH_COOKIE },
            { name: new RegExp(`^${GA_CLIENT_COOKIE}(_|$)`) },
          ],
        },
      },
      [EXPERIMENTS_CATEGORY]: {
        // OFF, on the same terms, and asked SEPARATELY. Being counted and being
        // shown a different page are different things to agree to, and one
        // switch over both would read the first answer as the second.
        enabled: false,
        // Both cookies are HttpOnly, so the library cannot actually reach
        // either — the server clears them on the request that reports the
        // change. It is stated anyway because the declaration is what the panel
        // shows the visitor about what withdrawing does.
        autoClear: {
          cookies: [{ name: VISITOR_COOKIE }],
        },
      },
    },

    language: {
      default: "en",
      // No `autoDetect`: detection would pick a language there is no
      // translation for and fall back anyway, and every extra translation is
      // another text somebody has to keep true.
      translations: {
        en: {
          consentModal: {
            title: "Cookies on this site",
            description:
              "We use cookies that are needed to run the site. Only if you allow it, we " +
              "also measure how the site is used – with our own tools and with Google " +
              "Analytics – and keep you on the same version of a page we are trying two " +
              "ways. Nothing you type and nothing from a document is ever measured.",
            acceptAllBtn: "Accept all",
            acceptNecessaryBtn: "Reject all",
            showPreferencesBtn: "Manage",
            // The control this labels REFUSES rather than postpones: the
            // library wires it to "accept necessary only", so closing the
            // banner records a refusal instead of leaving the question open and
            // asking again on the next page. The label says so.
            closeIconLabel: "Close and reject",
          },
          preferencesModal: {
            title: "Cookie settings",
            acceptAllBtn: "Accept all",
            acceptNecessaryBtn: "Reject all",
            savePreferencesBtn: "Save my choices",
            closeIconLabel: "Close",
            serviceCounterLabel: "Cookie|Cookies",
            sections: [
              {
                title: "What this is",
                description:
                  "The necessary cookies and the trial-version label are set by this site " +
                  "and kept on our own servers. Measurement is counted by our own servers " +
                  "and, if you allow it, by Google Analytics, which Google runs for us. You " +
                  "can change your answer at any time from the Cookie settings link at the " +
                  "bottom of any page, and changing it back takes the same one click as " +
                  "giving it.",
              },
              {
                title: "Strictly necessary",
                description:
                  "Keeps you signed in, carries a registration between two pages, and " +
                  "remembers the answer you give here so you are not asked again. The site " +
                  "cannot work without these, so they are always on.",
                linkedCategory: "necessary",
                cookieTable: {
                  headers: COOKIE_TABLE_HEADERS,
                  ...cookieTableFor("necessary"),
                },
              },
              {
                title: "Measurement",
                description:
                  "One cookie recording the site that referred you, the campaign parameters " +
                  "on the address you arrived at and the page you landed on, so a later " +
                  `registration can be credited to the channel that earned it. It is kept ` +
                  `for ${FIRST_TOUCH_DAYS} days. Allowing this also lets us count which ` +
                  "pages are read and which parts of them are used, and turns on Google " +
                  "Analytics, which Google runs for us: it sets its own cookies to count " +
                  "visits and receives the page address, your browser and device type and " +
                  "an approximate location. It is never used for advertising. We never " +
                  "record what you type, and never anything from a document you put into " +
                  "the demo.",
                linkedCategory: ANALYTICS_CATEGORY,
                cookieTable: {
                  headers: COOKIE_TABLE_HEADERS,
                  ...cookieTableFor(ANALYTICS_CATEGORY),
                },
              },
              {
                title: "Trying out changes",
                description:
                  "Sometimes we show a page two ways to find out which one is clearer. " +
                  "Allowing this lets us give you one of them and keep giving you the same " +
                  "one, using a random label with no name or address in it. It changes how " +
                  "a page looks, never what it costs you or what you can do. If you are " +
                  "signed in we use your account instead and store nothing on your device.",
                linkedCategory: EXPERIMENTS_CATEGORY,
                cookieTable: {
                  headers: COOKIE_TABLE_HEADERS,
                  ...cookieTableFor(EXPERIMENTS_CATEGORY),
                },
              },
            ],
          },
        },
      },
    },
  };
}
